import { Link } from "react-router-dom";
import { Gift, Landmark, BadgeCheck, MapPin, Factory, PackageCheck, Clock, HeartHandshake, Sparkles, ChevronRight, Coffee, Leaf } from "lucide-react";
import { PERKS } from "@/lib/perks";
import { MugWall } from "@/components/merch/MugWall";

const PHOTOS = ["/merch/tasse-gold-lifestyle.jpg", "/merch/tasse-gold-haende.jpg", "/merch/tasse-gold-geschenk.jpg", "/merch/tasse-gold-paar.jpg"];
const WHEN = [
  [Landmark, "Mindestens 100 € auf einmal", "Überweise mit deinem persönlichen Code als Verwendungszweck – so ordnen wir dich eindeutig zu."],
  [BadgeCheck, "Wir bestätigen den Eingang", "Sobald das Geld da ist, bekommst du eine Push-Nachricht und die Tasse wird dir geschenkt."],
  [MapPin, "Du gibst deine Adresse ein", "Direkt in der App unter „Meine Dankeschön-Tasse“ – zu jedem Schritt bekommst du Push und E-Mail."],
  [Factory, "Produktion nur für dich", "Wir geben deine Tasse in Produktion. Bei jedem Schritt bekommst du eine Push-Nachricht."],
  [PackageCheck, "Ankommen & genießen", "Weltweit kostenlos mit Sendungsnummer zu dir nach Hause – Lieferzeit 14–21 Tage, ins Ausland 21–35 Tage."],
];
const WHY = [
  [HeartHandshake, "Ein Dankeschön, kein Kauf", "Die Tasse ist ein Geschenk als Dank für deine freiwillige Unterstützung. Deine Unterstützung ist kein Kaufpreis für die Tasse."],
  [Leaf, "Erst produziert, wenn du sie bekommst", "Wir lassen jede Tasse erst nach der Bestellung herstellen. So halten wir Produktions- und Lagerkosten gering – und mehr von deiner Unterstützung fließt in die Community."],
  [Clock, "Lieferzeit 14–21 Tage", "Ins Ausland 21–35 Tage. Durch die Einzelproduktion dauert es etwas – dafür ist deine Tasse extra für dich gemacht."],
];

const Head = ({ eyebrow, title, sub, dark }) => (
  <div className="space-y-1.5">
    <p className={`text-[11px] font-extrabold uppercase tracking-[0.25em] ${dark ? "text-amber-300" : "text-amber-600"}`}>{eyebrow}</p>
    <h2 className={`font-display text-2xl sm:text-3xl font-black ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
    {sub && <p className={`text-sm sm:text-base ${dark ? "text-white/70" : "text-slate-500"}`}>{sub}</p>}
  </div>
);

function Hero() {
  return (
    <header className="relative overflow-hidden rounded-[2rem] bg-[#0b0b0b] text-white ring-1 ring-amber-300/30 shadow-2xl shadow-amber-500/20" data-testid="mug-present-hero">
      <span className="absolute -left-16 top-1/2 w-96 h-96 rounded-full bg-amber-400/25 blur-3xl mug-glow" aria-hidden />
      <div className="relative grid md:grid-cols-[1fr_1.1fr] items-center gap-6 p-6 sm:p-10">
        <div className="relative mx-auto w-full max-w-sm">
          <img src="/merch/tasse-gold.jpg" alt="Dankeschön-Tasse Black & Gold" className="w-full aspect-square object-cover rounded-[2rem] mug-glow-img" data-testid="mug-present-hero-img" />
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-rose-500 text-white text-[11px] font-black uppercase tracking-wider shadow-lg">Neu</span>
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-200 to-amber-500 text-[#3a2508] text-[11px] font-black uppercase tracking-wider"><Sparkles className="w-3 h-3" /> Limitiert</span>
        </div>
        <div className="space-y-4">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5"><Gift className="w-4 h-4" /> Exklusiv für Projekt Unterstützer</p>
          <h1 className="hero-plain font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] bg-gradient-to-r from-amber-100 via-amber-300 to-amber-500 bg-clip-text text-transparent">Tasse „Black &amp; Gold“</h1>
          <p className="text-base text-white/75 leading-relaxed">Mattschwarz, mit unserem goldenen Herz-Logo – unser persönliches Dankeschön an alle, die die Community mit <b className="text-amber-300">mindestens 100 € auf einmal</b> unterstützen. Kostenlos zu dir nach Hause geschickt.</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/community-unterstuetzen#bank-support" className="h-12 px-6 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#3a2508] font-black inline-flex items-center gap-2 active:scale-95 transition-transform" data-testid="mug-present-cta">Jetzt unterstützen <ChevronRight className="w-4 h-4" /></Link>
            <Link to="/dankeschoen-tasse" className="h-12 px-5 rounded-full border border-amber-300/50 text-amber-200 font-bold inline-flex items-center gap-2 hover:bg-white/5" data-testid="mug-present-track"><Coffee className="w-4 h-4" /> Meine Tasse verfolgen</Link>
          </div>
        </div>
      </div>
    </header>
  );
}

const Gallery = () => (
  <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mug-present-gallery">
    {PHOTOS.map((src, i) => <img key={src} src={src} alt="" loading="lazy" className={`w-full aspect-square object-cover rounded-3xl fade-up ${i % 2 ? "sm:translate-y-6" : ""}`} style={{ animationDelay: `${i * 80}ms` }} />)}
  </section>
);

const When = () => (
  <section className="space-y-5" data-testid="mug-present-when">
    <Head eyebrow="Wann du sie bekommst" title="So kommt die Tasse zu dir" sub="Fünf Schritte – alles direkt in der App." />
    <ol className="relative space-y-3 before:absolute before:left-5 before:top-2 before:bottom-2 before:w-px before:bg-amber-200">
      {WHEN.map(([Icon, t, x], i) => (
        <li key={t} className="relative flex gap-4 fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`mug-present-step-${i}`}>
          <span className="relative z-10 w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-[#3a2508] flex items-center justify-center shadow-md shadow-amber-300/40"><Icon className="w-5 h-5" /></span>
          <div className="pt-1"><p className="font-extrabold text-slate-900">{t}</p><p className="text-sm text-slate-500">{x}</p></div>
        </li>
      ))}
    </ol>
  </section>
);

const Why = () => (
  <section className="rounded-[2rem] bg-[#121212] p-6 sm:p-8 ring-1 ring-amber-300/25 space-y-5" data-testid="mug-present-why">
    <Head dark eyebrow="Warum" title="Warum wir dir eine Tasse schenken" sub="Weil du die Community trägst – und wir uns von Herzen bedanken möchten." />
    <div className="grid sm:grid-cols-3 gap-3">
      {WHY.map(([Icon, t, x]) => (
        <div key={t} className="rounded-2xl bg-white/5 border border-amber-300/20 p-4 space-y-2">
          <Icon className="w-6 h-6 text-amber-300" />
          <p className="font-bold text-white">{t}</p>
          <p className="text-sm text-white/65 leading-relaxed">{x}</p>
        </div>
      ))}
    </div>
  </section>
);

const Perks = () => (
  <section className="space-y-5" data-testid="mug-present-perks">
    <Head eyebrow="Noch mehr Dankeschön" title="Deine weiteren Vorteile als Projekt Unterstützer" sub="Die Tasse ist nur ein Teil – dein Konto wird zum Premium-Account:" />
    <ul className="grid sm:grid-cols-2 gap-3">
      {PERKS.map(([Icon, t, x], i) => (
        <li key={t} className="rounded-2xl bg-white border border-pink-100 p-4 flex gap-3 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`mug-present-perk-${i}`}>
          <span className="w-10 h-10 shrink-0 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center"><Icon className="w-5 h-5" /></span>
          <div><p className="font-bold text-slate-900">{t}</p><p className="text-sm text-slate-500">{x}</p></div>
        </li>
      ))}
    </ul>
    <p className="text-xs text-slate-400">Ab 25 € schalten wir dich 7 Tage als Projekt Unterstützer frei – ab 100 € auf einmal kommt die Tasse dazu.</p>
  </section>
);

export default function MugPresentation() {
  return (
    <div className="max-w-4xl mx-auto space-y-12 sm:space-y-16 pb-10" data-testid="mug-presentation-page">
      <Hero />
      <Gallery />
      <When />
      <Why />
      <Perks />
      <MugWall />
      <div className="rounded-[2rem] pink-gradient p-6 sm:p-8 text-white text-center space-y-3" data-testid="mug-present-final">
        <p className="font-display text-2xl font-black">Danke, dass du Teil unserer Geschichte bist 💛</p>
        <Link to="/community-unterstuetzen#bank-support" className="inline-flex h-12 px-6 rounded-full bg-white text-rose-600 font-black items-center gap-2" data-testid="mug-present-final-cta">Projekt unterstützen <ChevronRight className="w-4 h-4" /></Link>
        <p className="text-xs text-white/80"><Link to="/teilnahmebedingungen" className="underline" data-testid="mug-present-terms">Teilnahmebedingungen für Projekt Unterstützer</Link></p>
      </div>
    </div>
  );
}
